/**
 * Utilities for blog page rendering: TOC extraction, slug generation.
 */

export interface TocHeading {
  id: string
  text: string
  level: number
}

/**
 * Parse an HTML string and extract h2/h3 elements that have an `id` attribute.
 * Uses DOMParser (browser API) with a regex fallback for SSR/test environments.
 * Returns headings in document order.
 */
export function extractTocHeadings(html: string): TocHeading[] {
  if (!html) return []

  // Browser path — full DOM parsing preserves order correctly
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(
      `<div id="toc-root">${html}</div>`,
      'text/html'
    )
    const root = doc.getElementById('toc-root')
    if (!root) return []

    const results: TocHeading[] = []
    const elements = root.querySelectorAll('h2[id], h3[id]')
    elements.forEach((el) => {
      const id = el.getAttribute('id')
      if (!id) return
      const text = el.textContent?.trim() ?? ''
      const level = el.tagName === 'H2' ? 2 : 3
      results.push({ id, text, level })
    })
    return results
  }

  // Regex fallback (Node.js / test environments without DOM)
  const results: TocHeading[] = []
  const pattern = /<h([23])[^>]*\sid="([^"]+)"[^>]*>([\s\S]*?)<\/h\1>/gi
  let match: RegExpExecArray | null
  while ((match = pattern.exec(html)) !== null) {
    const level = parseInt(match[1], 10) as 2 | 3
    const id = match[2]
    // Strip inner HTML tags to get text content
    const text = match[3].replace(/<[^>]+>/g, '').trim()
    results.push({ id, text, level })
  }
  return results
}

/**
 * Convert a heading text into a URL-friendly id slug.
 * e.g. "What SPF permerror actually means" → "what-spf-permerror-actually-means"
 */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}
